import React, { useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Bookmark, Trash2, Star, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const FALLBACK = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600";

export default function MovieCard({ movie, groups, onLocalUpdate, onLocalDelete, onOpen }) {
  const [busy, setBusy] = useState(false);
  // Local optimistic state so the card never visibly "shifts"
  const [local, setLocal] = useState(movie);

  // Sync when parent passes an updated movie object (e.g. after refresh metadata)
  React.useEffect(() => { setLocal(movie); }, [movie]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const next = local.status === "watched" ? "backlog" : "watched";
    // Optimistic flip
    const optimistic = { ...local, status: next, watched_at: next === "watched" ? new Date().toISOString() : null };
    setLocal(optimistic);
    onLocalUpdate?.(optimistic);
    try {
      const res = await api.post(`/movies/${local.id}/toggle-watched`);
      setLocal(res.data);
      onLocalUpdate?.(res.data);
    } catch {
      // revert
      setLocal(local);
      onLocalUpdate?.(local);
      toast.error("Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${local.title}"?`)) return;
    onLocalDelete?.(local.id);
    try {
      await api.delete(`/movies/${local.id}`);
    } catch {
      toast.error("Failed to delete");
    }
  };

  const groupBadges = (local.group_ids || [])
    .map(id => groups.find(g => g.id === id))
    .filter(Boolean)
    .slice(0, 2);

  const seasons = local.seasons || [];
  const watchedSeasons = seasons.filter(s => s.status === "watched").length;
  const isTV = local.media_type === "tv" && seasons.length > 0;

  return (
    <div className="poster-card relative group" data-testid={`movie-card-${local.id}`}>
      <div
        className="relative aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1A1E] cursor-pointer"
        onClick={(e) => { if (!busy) onOpen?.(local); }}
        data-testid={`open-detail-${local.id}`}
      >
        <img
          src={local.poster || FALLBACK}
          alt={local.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = FALLBACK; }}
        />
        {local.status === "watched" && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity">
            <CheckCircle2 className="w-10 h-10 text-[#10B981]" />
          </div>
        )}
        {isTV && (
          <div className="absolute top-2 left-2 bg-black/70 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wider"
               data-testid={`seasons-chip-${local.id}`}>
            {watchedSeasons === seasons.length
              ? <span className="text-[#10B981]">ALL · {seasons.length}S</span>
              : <span><span className="text-[#10B981]">{watchedSeasons}</span><span className="text-slate-400">/{seasons.length} S</span></span>}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
          <div className="flex gap-2 mb-2">
            <button
              onClick={(e) => { e.stopPropagation(); toggle(); }}
              disabled={busy}
              data-testid={`toggle-watched-${local.id}`}
              className="flex-1 bg-white/10 hover:bg-[#E11D48] backdrop-blur-sm rounded-full px-3 py-2 text-xs font-medium flex items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
            >
              {local.status === "watched" ? <><Bookmark className="w-3.5 h-3.5" /> Move to backlog</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Mark watched</>}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button onClick={(e) => e.stopPropagation()} className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full w-8 h-8 flex items-center justify-center" data-testid={`movie-menu-${local.id}`}>
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent onClick={(e) => e.stopPropagation()} className="bg-[#0F0F12] border-white/10 text-slate-200">
                <DropdownMenuItem onClick={remove} className="text-rose-400 focus:text-rose-300 cursor-pointer" data-testid={`delete-movie-${local.id}`}>
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {local.imdb_rating && local.imdb_rating !== "N/A" && parseFloat(local.imdb_rating) > 0 && (
          <div className="absolute top-2 right-2 bg-black/70 backdrop-blur rounded-full px-2 py-0.5 text-xs flex items-center gap-1" data-testid={`rating-chip-${local.id}`}>
            <Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" /> {Number(parseFloat(local.imdb_rating).toFixed(1))}
          </div>
        )}
      </div>

      <div className="mt-2.5">
        <div className="text-sm font-medium line-clamp-1" title={local.title}>{local.title}</div>
        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
          <span>{local.year}</span>
          {groupBadges.length > 0 && (
            <>
              <span>·</span>
              <span className="flex gap-1">
                {groupBadges.map(g => (
                  <span key={g.id} className="px-1.5 py-0.5 rounded-full text-[10px] border border-white/10" style={{ color: g.color }}>{g.name}</span>
                ))}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
